import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './MapDialog.css';

const DEFAULT_ACCENT = '#ef4444';

export default function MapDialog({
  open,
  onClose,
  onConfirm,
  kicker,
  title,
  body,
  accentColor = DEFAULT_ACCENT,
  primaryLabel,
  cancelLabel = 'Cancel',
  loading = false,
}) {
  const [target, setTarget] = useState(null);
  const isConfirm = typeof onConfirm === 'function';
  const resolvedPrimaryLabel = primaryLabel || (isConfirm ? 'Confirm' : 'Got it');

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      setTarget(null);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let raf = null;

    const findTarget = () => {
      if (cancelled) return;
      const el = document.querySelector('.deck-map-area') || document.body;
      setTarget(el);
      if (el === document.body && attempts < 60) {
        attempts += 1;
        raf = requestAnimationFrame(findTarget);
      }
    };

    findTarget();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open]);

  if (!open || !target) {
    return null;
  }

  const handlePrimary = () => {
    if (loading) return;
    if (isConfirm) {
      onConfirm?.();
      return;
    }
    onClose?.();
  };

  const panel = (
    <div className="map-dialog-viewport">
      <div className="map-dialog-scrim">
        <section
          className="map-dialog-panel"
          role="dialog"
          aria-modal="true"
          aria-label={title || kicker || 'Map dialog'}
          style={{ '--map-dialog-accent': accentColor }}
        >
          {kicker ? <div className="map-dialog-kicker">{kicker}</div> : null}
          {title ? <h2 className="map-dialog-title">{title}</h2> : null}
          {body ? <div className="map-dialog-body">{body}</div> : null}

          <footer className="map-dialog-actions">
            {isConfirm ? (
              <button
                type="button"
                className="map-dialog-button map-dialog-button--ghost"
                onClick={onClose}
              >
                {cancelLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="map-dialog-button map-dialog-button--primary"
              disabled={loading}
              onClick={handlePrimary}
            >
              {loading ? <span className="map-dialog-spinner" aria-hidden="true" /> : null}
              <span>{resolvedPrimaryLabel}</span>
            </button>
          </footer>
        </section>
      </div>
    </div>
  );

  return createPortal(panel, target);
}
